/**
 * Droits d'accès : une ligne par produit (entrée journalière, carte annuelle, stationnement…), une case par catégorie
 * de l'échelle (ou un seul prix pour un véhicule, une embarcation). Case vide = non offert, 0 = gratuit,
 * « sd » = sur demande. En bas : l'aperçu du prix d'un groupe (« 2 adultes, 1 enfant de 2 ans, en voiture »).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteAccessLine, fetchTariffs, quoteTariffs, requestTariffListItem, saveAccessLine, type AccessLine, type QuoteResult } from '../../api'
import { cellText, errorText, numOrNull, parseCell, type TabProps } from './common'

const PER_PERSON = 'person'

export function AccessTab({ orgId, data, onSaved, onListChanged }: TabProps) {
  const { t } = useTranslation('gestion')
  const [newProduct, setNewProduct] = useState('')
  const [askName, setAskName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = async () => {
    if (!newProduct) return
    try {
      onSaved(await saveAccessLine(orgId, null, { product_id: Number(newProduct), year: new Date().getFullYear() }))
      setNewProduct('')
    } catch (e) {
      setError(errorText(e))
    }
  }
  const ask = async () => {
    try {
      const item = await requestTariffListItem(orgId, 'products', askName.trim())
      onListChanged?.()
      onSaved(await saveAccessLine(orgId, null, { product_id: item.id, year: new Date().getFullYear() }))
      setAskName('')
    } catch (e) {
      setError(errorText(e))
    }
  }

  return (
    <>
      <p className="g-muted">{t('tarifs.acces_aide')}</p>
      {data.scale.length === 0 && <p className="g-alert">{t('tarifs.echelle_vide')}</p>}
      {data.access_lines.length === 0 && <p className="g-hint">{t('tarifs.aucune_ligne')}</p>}
      {data.access_lines.map((line) => (
        <AccessLineEditor key={line.id} orgId={orgId} data={data} line={line} onSaved={onSaved} />
      ))}
      <div className="g-tarifs__request">
        <select className="g-input" value={newProduct} onChange={(e) => setNewProduct(e.target.value)}>
          <option value="">{t('tarifs.choisir_produit')}</option>
          {data.lists.products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.pending ? ` (${t('tarifs.en_attente')})` : ''}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn--primary" disabled={!newProduct} onClick={() => void add()}>
          <span className="mdi mdi-plus" aria-hidden /> {t('tarifs.ajouter_ligne')}
        </button>
        <span className="g-muted">{t('tarifs.produit_manquant')}</span>
        <input className="g-input" value={askName} placeholder={t('tarifs.nouveau_produit')} onChange={(e) => setAskName(e.target.value)} />
        <button type="button" className="btn" disabled={!askName.trim()} onClick={() => void ask()}>
          {t('tarifs.demander')}
        </button>
      </div>
      {error && <p className="g-alert g-alert--error">{error}</p>}
      <QuotePanel orgId={orgId} data={data} />
    </>
  )
}

function AccessLineEditor({ orgId, data, line, onSaved }: { orgId: number; data: TabProps['data']; line: AccessLine; onSaved: TabProps['onSaved'] }) {
  const { t } = useTranslation('gestion')
  const includeTax = data.settings.prices_include_tax
  const byCat = new Map(line.cells.map((c) => [c.category_id, c]))
  const [unit, setUnit] = useState(line.unit)
  const [season, setSeason] = useState(line.season)
  const [validity, setValidity] = useState(line.validity)
  const [days, setDays] = useState(line.validity_days?.toString() ?? '')
  const [startingFrom, setStartingFrom] = useState(line.starting_from)
  const [note, setNote] = useState(line.note)
  const [cells, setCells] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = { all: cellText(byCat.get(null), includeTax) }
    for (const c of data.scale) out[c.id] = cellText(byCat.get(c.id), includeTax)
    return out
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const perPerson = unit === PER_PERSON

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const cellInputs = perPerson
        ? [parseCell(cells.all ?? '', null), ...data.scale.map((c) => parseCell(cells[c.id] ?? '', c.id))]
        : [parseCell(cells.all ?? '', null)]
      onSaved(await saveAccessLine(orgId, line.id, { unit, season, validity, validity_days: numOrNull(days), starting_from: startingFrom, note, cells: cellInputs }))
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  const remove = async () => {
    if (!window.confirm(t('tarifs.supprimer_ligne', { name: line.product }))) return
    await deleteAccessLine(orgId, line.id)
    onSaved(await fetchTariffs(orgId))
  }

  return (
    <div className="g-tarifs__line">
      <div className="g-tarifs__line-head">
        <strong>{line.product}</strong>
        <select className="g-input" value={unit} onChange={(e) => setUnit(e.target.value)} aria-label={t('tarifs.unite')}>
          {data.choices.units.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select className="g-input" value={season} onChange={(e) => setSeason(e.target.value)} aria-label={t('tarifs.saison')}>
          {data.choices.seasons.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select className="g-input" value={validity} onChange={(e) => setValidity(e.target.value)} aria-label={t('tarifs.validite')}>
          {data.choices.validities.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        {validity === 'duration' && (
          <input className="g-input g-tarifs__num" inputMode="numeric" value={days} placeholder={t('tarifs.jours')} onChange={(e) => setDays(e.target.value)} />
        )}
        <label className="g-check">
          <input type="checkbox" checked={startingFrom} onChange={(e) => setStartingFrom(e.target.checked)} />
          <span>{t('tarifs.a_partir_de')}</span>
        </label>
      </div>
      <div className="g-tarifs__cells">
        {perPerson ? (
          <>
            {data.scale.map((c) => (
              <label key={c.id} className="g-field">
                <span className="g-field__label">{c.label}</span>
                <input className="g-input" value={cells[c.id] ?? ''} placeholder="—" onChange={(e) => setCells((x) => ({ ...x, [c.id]: e.target.value }))} />
              </label>
            ))}
            <label className="g-field">
              <span className="g-field__label">{t('tarifs.tous')}</span>
              <input className="g-input" value={cells.all ?? ''} placeholder="—" onChange={(e) => setCells((x) => ({ ...x, all: e.target.value }))} />
            </label>
          </>
        ) : (
          <label className="g-field">
            <span className="g-field__label">{t('tarifs.prix')}</span>
            <input className="g-input" value={cells.all ?? ''} placeholder="—" onChange={(e) => setCells((x) => ({ ...x, all: e.target.value }))} />
          </label>
        )}
      </div>
      <label className="g-field">
        <span className="g-field__label">{t('tarifs.conditions')}</span>
        <input className="g-input" value={note} placeholder={t('tarifs.conditions_exemple')} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="g-editor__buttons">
        <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void save()}>
          {busy ? t('commun.enregistrement') : t('commun.enregistrer')}
        </button>
        <span className="g-spacer" />
        <button type="button" className="btn g-danger" onClick={() => void remove()}>
          {t('commun.supprimer')}
        </button>
      </div>
      {error && <p className="g-alert g-alert--error">{error}</p>}
    </div>
  )
}

type Person = { key: number; age: string; status: string }

function QuotePanel({ orgId, data }: { orgId: number; data: TabProps['data'] }) {
  const { t } = useTranslation('gestion')
  const statuses = data.scale.filter((c) => c.kind === 'status')
  const titleSlug = new Map(data.lists.categories.map((x) => [x.id, x.slug]))
  const [people, setPeople] = useState<Person[]>([
    { key: 1, age: '40', status: '' },
    { key: 2, age: '38', status: '' },
    { key: 3, age: '8', status: '' },
  ])
  const [vehicles, setVehicles] = useState('1')
  const [boats, setBoats] = useState('0')
  const [result, setResult] = useState<QuoteResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    setError(null)
    try {
      setResult(
        await quoteTariffs(
          orgId,
          people.map((p, i) => ({ age: numOrNull(p.age), status: p.status, label: t('tarifs.personne_n', { n: i + 1 }) })),
          numOrNull(vehicles) ?? 0,
          numOrNull(boats) ?? 0,
        ),
      )
    } catch (e) {
      setError(errorText(e))
    }
  }

  return (
    <div className="g-tarifs__quote">
      <h2 className="g-editor__section">{t('tarifs.apercu_titre')}</h2>
      <p className="g-muted">{t('tarifs.apercu_aide')}</p>
      {people.map((p, i) => (
        <div key={p.key} className="g-tarifs__person">
          <span>{t('tarifs.personne_n', { n: i + 1 })}</span>
          <input className="g-input g-tarifs__num" inputMode="numeric" value={p.age} placeholder={t('tarifs.age')} onChange={(e) => setPeople((ps) => ps.map((x) => (x.key === p.key ? { ...x, age: e.target.value } : x)))} />
          {statuses.length > 0 && (
            <select className="g-input" value={p.status} onChange={(e) => setPeople((ps) => ps.map((x) => (x.key === p.key ? { ...x, status: e.target.value } : x)))}>
              <option value="">{t('tarifs.aucun_statut')}</option>
              {statuses.map((s) => (
                <option key={s.id} value={titleSlug.get(s.category_id ?? 0) ?? ''}>
                  {s.label}
                </option>
              ))}
            </select>
          )}
          <button type="button" className="g-link g-danger" onClick={() => setPeople((ps) => ps.filter((x) => x.key !== p.key))}>
            {t('commun.supprimer')}
          </button>
        </div>
      ))}
      <div className="g-tarifs__person">
        <button type="button" className="btn" onClick={() => setPeople((ps) => [...ps, { key: Date.now(), age: '', status: '' }])}>
          <span className="mdi mdi-plus" aria-hidden /> {t('tarifs.ajouter_personne')}
        </button>
        <label>
          {t('tarifs.vehicules')} <input className="g-input g-tarifs__num" inputMode="numeric" value={vehicles} onChange={(e) => setVehicles(e.target.value)} />
        </label>
        <label>
          {t('tarifs.embarcations')} <input className="g-input g-tarifs__num" inputMode="numeric" value={boats} onChange={(e) => setBoats(e.target.value)} />
        </label>
        <button type="button" className="btn btn--primary" onClick={() => void run()}>
          {t('tarifs.calculer')}
        </button>
      </div>
      {error && <p className="g-alert g-alert--error">{error}</p>}
      {result && (
        <div className="g-tarifs__result">
          {result.lines
            .filter((l) => Number(l.amount) > 0 || l.on_request || l.missing.length > 0 || l.detail.length > 0)
            .map((l) => (
              <div key={l.line_id}>
                <strong>
                  {l.product} : {l.amount} ${l.on_request ? ` + ${t('tarifs.sur_demande')}` : ''}
                </strong>
                <span className="g-muted"> {l.detail.join(' · ')}</span>
                {l.missing.length > 0 && <span className="g-tarifs__missing"> {l.missing.join(' · ')}</span>}
              </div>
            ))}
          <p>
            {t('tarifs.total', { avant: result.before_tax, taxes: result.taxes, total: result.total })}
          </p>
        </div>
      )}
    </div>
  )
}
